import { Dayjs } from 'dayjs';

import { SBaseDisplay, SDate } from '@summit/core';
import { ISMapPolyLine, SMapPolyline } from '@summit/maps';

import { SummitFormattedText } from '@summit/models';
import { EboObject } from '../../base-classes/ebo-object';
import { Employee } from '../../employee/employee';
import { PayType } from '../../payroll/shared/pay-type.enum';
import { Hub } from '../../places/hub/hub';
import { SpotAsk } from '../../spot/spot-ask';
import { PositionReport } from '../../truck/position-report/position-report';
import { TruckAlias } from '../../truck/truck-alias';
import { Ask } from '../asks/ask';
import { GpsFlags } from './gps-flags.enum';

export class GpsLeg extends EboObject implements SBaseDisplay {
  aliasID: string;
  alias: TruckAlias;
  originationDateTime: Dayjs;
  originationPositionReportId: string;
  originationPositionReport: PositionReport;
  origNum: number;
  origHub: Hub;
  destinationDateTime: Dayjs;
  destinationPositionReportId: string;
  destinationPositionReport: PositionReport;
  destNum: number;
  destHub: Hub;
  miles: number;
  employeeID: string;
  employee: Employee;
  teammateID: string;
  teammate: Employee;
  flags: GpsFlags;
  tripID: string;
  trip: Ask;
  spotAskID: string;
  spotAsk: SpotAsk;
  positionReports: PositionReport[];

  constructor(jsonData: Partial<GpsLeg>) {
    super(jsonData);
    if (jsonData) {
      //console.log("assigning object", jsonData)
      if (jsonData.alias) {
        this.alias = new TruckAlias(jsonData.alias);
      }
      if (jsonData.originationDateTime) {
        this.originationDateTime = SDate.newUtc(jsonData.originationDateTime);
      }
      if (jsonData.originationPositionReport) {
        this.originationPositionReport = new PositionReport(jsonData.originationPositionReport);
      }
      if (jsonData.origHub) {
        this.origHub = new Hub(jsonData.origHub);
      }
      if (jsonData.destinationDateTime) {
        this.destinationDateTime = SDate.newUtc(jsonData.destinationDateTime);
      }
      if (jsonData.destinationPositionReport) {
        this.destinationPositionReport = new PositionReport(jsonData.destinationPositionReport);
      }
      if (jsonData.destHub) {
        this.destHub = new Hub(jsonData.destHub);
      }
      if (jsonData.employee) {
        this.employee = new Employee(jsonData.employee);
      }
      if (jsonData.teammate) {
        this.teammate = new Employee(jsonData.teammate);
      }
      if (jsonData.trip) {
        this.trip = new Ask(jsonData.trip);
      }
      if (jsonData.spotAsk) {
        this.spotAsk = new SpotAsk(jsonData.spotAsk);
      }
      if (jsonData.positionReports) {
        this.positionReports = jsonData.positionReports.map((item) => new PositionReport(item));
      }
    }
  }

  get eventDate(): Dayjs {
    return SDate.new(this.originationDateTime).startOf('day');
  }

  get defaultSort(): string {
    return `${this.originationDateTime.format('YYYYMMDDHHmmss')}-${this.destinationDateTime.format('YYYYMMDDHHmmss')}`;
  }

  get fedExID(): string {
    return this.employee?.fedExID;
  }

  get linked(): boolean {
    if (this.tripID || this.spotAskID) return true;
    return false;
  }

  get payType(): PayType {
    return this.teammateID ? PayType.Team : PayType.Solo;
  }

  GetPolyLine(): ISMapPolyLine {
    const response = new SMapPolyline();
    response.points = this.positionReports.map((item) => item.point);
    return response;
  }

  HasFlag(flag: GpsFlags): boolean {
    // eslint-disable-next-line no-bitwise
    return flag === (this.flags & flag);
  }

  SetFlag(flag: GpsFlags, value: boolean) {
    if (value) {
      // eslint-disable-next-line no-bitwise
      this.flags |= flag;
    } else {
      // eslint-disable-next-line no-bitwise
      this.flags &= ~flag;
    }
  }

  toShortString(): string {
    return `${this.originationDateTime?.format('M/D HH:mm')}-${this.destinationDateTime?.format('HH:mm')}  = ${this.miles}`;
  }

  toPartialTrip(): Partial<Ask> {
    const response: Partial<Ask> = {
      employeeID: this.employeeID,
      origNum: this.origNum,
      destNum: this.destNum,
      eventDate: SDate.new(this.originationDateTime).startOf('day'),
      truckNum: this.alias.name,
      miles: Math.round(this.miles),
      payTypeID: this.payType
    };
    return response;
  }

  static SortByTravel(inList: GpsLeg[], startHub: number): GpsLeg[] {
    if (inList.length <= 1) return inList;
    if (inList.length == 2) {
      if (inList[0].origNum == startHub) return inList;
      return inList.reverse();
    }
    const inputList = [...inList];
    let responseList = new Array<GpsLeg>();
    let nextLegIndex = inputList.findIndex((entry) => entry.origNum == startHub);
    if (nextLegIndex < 0) nextLegIndex = 0;
    const nextLegs = inputList.splice(nextLegIndex, 1);
    responseList.push(nextLegs[0]);
    const remainingLegs = GpsLeg.SortByTravel(inputList, nextLegs[0].destNum);
    responseList = responseList.concat(remainingLegs);
    return responseList;
  }

  get milesFormatted(): SummitFormattedText {
    const response = new SummitFormattedText();
    if (this.HasFlag(GpsFlags.OdometerEstimated)) {
      if (this.HasFlag(GpsFlags.OdometerStuck)) {
        //Crow Flies
        response.AddLine(`>${this.miles}`);
      } else {
        //Average
        response.AddLine(`~${this.miles}`);
      }
    } else {
      response.AddLine(`${this.miles}`);
    }
    return response;
  }
}
